import React from "react";
import theme from "../data/theme";
import {
  POSITIONS, POSITION_DIAL_RANGE, DEFAULT_POSITION_DIALS, describePositionDials,
} from "../utils/positionShares";

const { colors, spacing, radius, fontSize, fontWeight } = theme;

/**
 * PositionDials — four sliders that say "quarterbacks go for more in my league".
 *
 * 🔴 THE LABEL STATES THE CLAIM THE CODE SATISFIES, AND ONLY THAT.
 * Each slider reads "RB +20% vs other positions" — a RELATIVE claim, which is
 * exactly what the normalizer guarantees (it divides the whole vector by a
 * scalar, so ratios survive untouched). It does NOT say "20% of spend": that
 * would be a claim about shares, which this does not compute.
 *
 * This is the overpay slider's mistake, not repeated. That control is labelled
 * `%` at MockSetup.jsx:519 and applied as flat dollars at auction.js:1133 —
 * out of scope to fix here, but the reason this file is careful.
 *
 * The readout underneath shows the DERIVED effect on all four positions, from
 * describePositionDials, so the number on screen is the number the engine used
 * rather than a restatement of the slider.
 */
export default function PositionDials({ dials, onChange, overridesLeagueHistory }) {
  const current = dials || DEFAULT_POSITION_DIALS;
  const effect = describePositionDials(current);
  const touched = !!effect;

  const set = (pos, value) => onChange({ ...current, [pos]: value });
  const pct = (v) => Math.round((v - 1) * 100);
  const fmt = (n) => `${n >= 0 ? "+" : "−"}${Math.abs(n).toFixed(1)}%`;

  return (
    <div style={{ marginBottom: spacing.xxl }}>
      <div style={{
        display: "flex", alignItems: "center", justifyContent: "space-between",
        marginBottom: spacing.md, flexWrap: "wrap", gap: spacing.md,
      }}>
        <span style={{
          fontSize: fontSize.micro, fontWeight: fontWeight.bold,
          color: colors.text.secondary, letterSpacing: "1px",
        }}>POSITION PRICES</span>
        {touched && (
          <button
            onClick={() => onChange({ ...DEFAULT_POSITION_DIALS })}
            style={{
              background: "transparent", border: `1px solid ${colors.border.default}`,
              borderRadius: radius.sm, color: colors.text.muted, fontFamily: "inherit",
              fontSize: fontSize.micro, padding: "3px 10px", cursor: "pointer",
            }}
          >Reset</button>
        )}
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(210px, 1fr))", gap: spacing.lg }}>
        {POSITIONS.map(pos => {
          const v = current[pos] ?? 1;
          const p = pct(v);
          return (
            <div key={pos}>
              <div style={{
                display: "flex", justifyContent: "space-between", alignItems: "baseline",
                marginBottom: spacing.xs,
              }}>
                <span style={{
                  fontSize: fontSize.caption, fontWeight: fontWeight.heavy,
                  color: colors.pos[pos] || colors.text.secondary, letterSpacing: "0.5px",
                }}>{pos}</span>
                {/* The relative claim, spelled out. */}
                <span style={{
                  fontSize: fontSize.micro,
                  color: p === 0 ? colors.text.faint : colors.text.secondary,
                }}>
                  {p === 0 ? "no change" : `${p > 0 ? "+" : ""}${p}% vs other positions`}
                </span>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: spacing.md }}>
                <span style={{ fontSize: fontSize.nano, color: colors.text.muted }}>−25</span>
                <input
                  type="range"
                  aria-label={`${pos} price dial`}
                  min={POSITION_DIAL_RANGE.min} max={POSITION_DIAL_RANGE.max}
                  step={POSITION_DIAL_RANGE.step} value={v}
                  onChange={e => set(pos, parseFloat(e.target.value))}
                  style={{ flex: 1, accentColor: colors.pos[pos] || colors.brand.orange }}
                />
                <span style={{ fontSize: fontSize.nano, color: colors.text.muted }}>+25</span>
              </div>
            </div>
          );
        })}
      </div>

      {/* The derived effect, and the one line of copy that pre-empts "I raised
          RB and my WRs got cheaper, is that a bug?". It is the clearest
          in-product statement of why a simulator is not a printed cheat sheet. */}
      <div style={{
        marginTop: spacing.lg, padding: `${spacing.md}px ${spacing.lg}px`,
        background: colors.bg.raised, borderRadius: radius.md,
        border: `1px solid ${colors.border.subtle}`,
      }}>
        {touched ? (
          <>
            <div style={{ display: "flex", gap: spacing.lg, flexWrap: "wrap", marginBottom: spacing.xs }}>
              {POSITIONS.map(pos => (
                <span key={pos} style={{ fontSize: fontSize.caption, fontVariantNumeric: "tabular-nums" }}>
                  <strong style={{ color: colors.pos[pos] }}>{pos}</strong>{" "}
                  <span style={{
                    color: effect[pos] > 0.05 ? colors.brand.accent
                      : effect[pos] < -0.05 ? "#7dd3fc" : colors.text.faint,
                  }}>{fmt(effect[pos])}</span>
                </span>
              ))}
            </div>
            <div style={{ fontSize: fontSize.micro, color: colors.text.muted, lineHeight: 1.5 }}>
              The pot is fixed, so making one position pricier makes the rest cheaper.
              These are the numbers the room will actually bid to.
            </div>
          </>
        ) : (
          <div style={{ fontSize: fontSize.micro, color: colors.text.faint, lineHeight: 1.5 }}>
            All four at rest — prices are the standard board. Move one and the others adjust,
            because the pot is fixed.
          </div>
        )}
      </div>

      {/* 🔴 NEVER LET ONE WRITER SILENTLY DISPLACE THE OTHER. */}
      {overridesLeagueHistory && (
        <div style={{
          marginTop: spacing.md, padding: `${spacing.md}px ${spacing.lg}px`,
          background: "rgba(234,179,8,0.10)", border: "1px solid rgba(234,179,8,0.35)",
          borderRadius: radius.md, fontSize: fontSize.caption, color: "#fde68a", lineHeight: 1.5,
        }}>
          Your manual adjustments are overriding this league's imported history. Reset them to
          go back to the calibrated prices.
        </div>
      )}
    </div>
  );
}
